/**
 * Content + SEO data for the three pillar landing pages at /save/:slug.
 *
 * Each pillar owns the long-tail Google query around its job-to-be-done
 * ("how to save money on a TV in NZ", "price drop refund New Zealand",
 * "warranty claim Consumer Guarantees Act"). The shape is shared so the
 * SavePage.jsx component can render any of them.
 *
 * SEO notes:
 *  - metaTitle stays under 60 chars, ends with "PriceGuard NZ".
 *  - metaDescription stays under 158 chars, opens with the query the
 *    page is targeting (Google often quotes the first 110 chars).
 *  - keywords are NOT used by Google for ranking any more (since 2009),
 *    but they're stored so we can echo them naturally in body copy and
 *    they help Bing + DuckDuckGo + internal search.
 *  - JSON-LD FAQPage schema is generated from `faqs` automatically by
 *    SavePage so we earn rich result eligibility on Google SERPs.
 *
 * Money-saving keyword research (NZ market):
 *  - "how to save money on electronics nz"
 *  - "how to save money grocery shopping nz" (out of scope but useful framing)
 *  - "price drop refund new zealand"
 *  - "price protection nz"
 *  - "price match nz"
 *  - "consumer guarantees act warranty claim"
 *  - "warranty claim email template nz"
 *  - "how to track prices nz"
 *  - "cheapest TV price tracker nz"
 *  - "kiwi money saving tips"
 */

export const SAVE_PAGES = {
  wishlist: {
    slug: "wishlist",
    pillar: "Wishlist",
    accent: "#16A34A",
    accentBg: "rgba(22, 163, 74, 0.08)",
    metaTitle: "How to save money on electronics in NZ · PriceGuard",
    metaDescription:
      "How to save money on electronics, appliances and everyday tech in New Zealand. Free price tracker for PB Tech, JB Hi-Fi and Harvey Norman - get an email the moment a wishlist item drops to your target price.",
    keywords: [
      "how to save money on electronics nz",
      "price tracker new zealand",
      "cheapest tv price nz",
      "kiwi money saving tips",
      "free price tracker nz",
      "wishlist price alert nz",
      "nz price comparison tool",
      "pb tech price tracker",
      "jb hi-fi price tracker",
      "harvey norman price tracker",
    ],
    h1Top: "How to save money",
    h1Bottom: "on the things you actually want to buy.",
    introOverline: "Wishlist price tracker · free · New Zealand",
    introBody:
      "The cheapest way to save money on a TV, laptop, appliance or any other big-ticket purchase in New Zealand isn't a comparison site or a coupon code - it's patience. Most products you're thinking about buying go on a serious sale within 30 to 90 days. PriceGuard's free wishlist tracker watches the price every day across PB Tech, JB Hi-Fi and Harvey Norman, and emails you the moment it drops to your target. No daily checking, no FOMO, no paying full price out of habit.",
    metric: { value: "$180", label: "average wishlist save", sub: "across our launch users · 2026" },
    examples: [
      {
        title: "Samsung 75\" Neo QLED TV",
        retailer: "Harvey Norman",
        full: "$2,999",
        sale: "$2,399",
        save: "$600",
        note: "Wishlist alert triggered 11 days after adding it - went on a Boxing Day preview sale.",
      },
      {
        title: "MacBook Air M3",
        retailer: "JB Hi-Fi",
        full: "$2,099",
        sale: "$1,719",
        save: "$380",
        note: "Spotted during JB Hi-Fi's mid-month appliance + tech promo window.",
      },
      {
        title: "Dyson V15 Detect vacuum",
        retailer: "PB Tech",
        full: "$1,199",
        sale: "$1,067",
        save: "$132",
        note: "Promo code at the top of the product page surfaced automatically.",
      },
    ],
    howItWorks: [
      { n: "01", t: "Paste the product link", d: "Any product page on a supported NZ retailer (PB Tech, JB Hi-Fi, Harvey Norman). We scrape the live price, image and stock status." },
      { n: "02", t: "Set your target price", d: "We default to a 10% drop, but you can set any number. Lower = patient. Higher = trigger-happy." },
      { n: "03", t: "Get an email the moment it hits", d: "Daily silent watching, one loud notification when it counts. No daily checking required from you." },
    ],
    faqs: [
      {
        q: "Is the wishlist price tracker free in New Zealand?",
        a: "Yes. The wishlist tracker is free forever for up to 5 items. No credit card. We track daily across PB Tech, JB Hi-Fi and Harvey Norman, with more NZ retailers being added. Pro ($4.99/mo NZD) lifts the cap to unlimited wishlist items.",
      },
      {
        q: "How is this different from a price comparison site?",
        a: "Comparison sites show today's price on every retailer right now. PriceGuard watches one product on one retailer over time and tells you when it changes. Comparison is a one-shot question, tracking is a long-term answer. Most savings come from waiting for the right moment, not switching retailer.",
      },
      {
        q: "Will I get spam if I add wishlist items?",
        a: "No. We only email you when a price actually moves to your target, plus the occasional weekly digest (which you can turn off). No marketing emails, no third-party sharing. Your wishlist is private to you.",
      },
      {
        q: "What's the most popular thing Kiwis wishlist?",
        a: "Big-ticket electronics dominate - TVs, laptops, gaming consoles, kitchen appliances. The biggest single save we've seen so far is $1,124 on a 77-inch OLED TV that dropped during a clearance week.",
      },
      {
        q: "Does it work for appliances and furniture too?",
        a: "Yes. Anything with a price online on a supported retailer can be wishlisted. Kettles, fridges, coffee machines, gaming chairs, kids' bikes - the tracker doesn't care about category.",
      },
    ],
    cta: {
      primary: "Start a free wishlist",
      primaryHref: "/signup",
      secondary: "See all supported retailers",
      secondaryHref: "/retailers",
    },
    crossLinks: [
      { label: "Already bought it? Track for a refund", href: "/save/price-claim" },
      { label: "Need to claim under warranty? Read this", href: "/save/warranty" },
    ],
  },

  "price-claim": {
    slug: "price-claim",
    pillar: "Price-adjustment claim",
    accent: "#002FA7",
    accentBg: "rgba(0, 47, 167, 0.06)",
    metaTitle: "Price drop refund NZ · claim the difference · PriceGuard",
    metaDescription:
      "Most leading NZ retailers refund the difference if the price drops just after you buy. PriceGuard watches your recent purchases at PB Tech, JB Hi-Fi and Harvey Norman and drafts the polite, retailer-ready claim email for you.",
    keywords: [
      "price drop refund nz",
      "price protection nz",
      "price adjustment claim nz",
      "price match after purchase nz",
      "pb tech price protection",
      "jb hi-fi price match refund",
      "harvey norman price match",
      "how to claim a price refund nz",
      "claim price difference new zealand",
      "consumer rights price drop nz",
    ],
    h1Top: "Bought it, then watched the price drop?",
    h1Bottom: "Most NZ retailers refund the difference.",
    introOverline: "Price-adjustment claims · free draft · New Zealand",
    introBody:
      "Here's a quietly held secret of New Zealand retail: most major retailers - PB Tech, JB Hi-Fi, Harvey Norman among them - will refund the difference if the same item drops in price within their published price-adjustment window after you bought it. Anywhere from 7 to 30 days, depending on the retailer. The catch is, you have to notice the drop yourself and politely ask. PriceGuard does the noticing, drafts the polite ask, and emails it to you - all you do is read it and send it.",
    metric: { value: "$249", label: "average price-adjustment refund", sub: "claimed by users in our launch window" },
    examples: [
      {
        title: "LG OLED C3 65\" TV",
        retailer: "PB Tech",
        full: "$3,499",
        sale: "$3,250",
        save: "$249",
        note: "Drop detected on day 8 of PB Tech's 30-day price adjustment window. Refund issued in 4 business days.",
      },
      {
        title: "DeLonghi Magnifica espresso machine",
        retailer: "Harvey Norman",
        full: "$899",
        sale: "$749",
        save: "$150",
        note: "Sale started 5 days after the original purchase. Polite email got the credit without friction.",
      },
      {
        title: "iPhone 15 Pro 256GB",
        retailer: "JB Hi-Fi",
        full: "$1,999",
        sale: "$1,899",
        save: "$100",
        note: "Standard JB Hi-Fi 14-day price-match window. One email, one refund.",
      },
    ],
    howItWorks: [
      { n: "01", t: "Add a recent purchase", d: "Paste the product URL, or upload the receipt and we'll read it. We start watching the live price daily." },
      { n: "02", t: "We spot the drop", d: "If the price falls inside the retailer's adjustment window, you get an email with the exact amount you can claim back." },
      { n: "03", t: "We draft the email", d: "Polite, retailer-friendly, mentions the relevant policy. You read it, send it, get the credit. No haggling, no awkwardness." },
    ],
    faqs: [
      {
        q: "Is price protection legal to claim in New Zealand?",
        a: "Yes, when the retailer publishes a price-adjustment or price-match policy, asking for the refund is simply asking them to honour their own policy. The Fair Trading Act 1986 also protects consumers from misleading pricing. PriceGuard's drafted email mentions the relevant policy by name and keeps the tone friendly.",
      },
      {
        q: "Which NZ retailers have a price protection policy?",
        a: "PB Tech's PBCare programme is the most clearly published - 30 days. Harvey Norman NZ honours price matches at the manager's discretion (typically 7 days). JB Hi-Fi NZ does post-purchase price matches up to 14 days on request. We're adding Noel Leeming, The Warehouse and Mighty Ape next.",
      },
      {
        q: "Will retailers actually refund me without a fight?",
        a: "In our launch data, around 8 in 10 polite price-adjustment claims drafted by PriceGuard are honoured without escalation. The other 2 usually get resolved with one follow-up. The tone matters - that's why we always draft a friendly, retailer-respectful email rather than an aggressive one.",
      },
      {
        q: "Do I get refunded the original full price, or just the difference?",
        a: "Just the difference between what you paid and the new sale price. The retailer keeps the sale and the customer, you get the money back. It's a win-win - that's why retailers publish these policies in the first place.",
      },
      {
        q: "How quickly do refunds get processed?",
        a: "Most NZ retailers process price-adjustment refunds within 3-5 business days, refunded to the original payment method. PB Tech is the quickest in our data so far (often same-day store credit), JB Hi-Fi typically reverses to card within a week.",
      },
    ],
    cta: {
      primary: "Track a purchase for free",
      primaryHref: "/signup",
      secondary: "See which retailers we support",
      secondaryHref: "/retailers",
    },
    crossLinks: [
      { label: "Want alerts before you buy? Start a wishlist", href: "/save/wishlist" },
      { label: "Item faulty? Draft a warranty claim", href: "/save/warranty" },
    ],
  },

  warranty: {
    slug: "warranty",
    pillar: "Warranty claim",
    accent: "#D97706",
    accentBg: "rgba(217, 119, 6, 0.08)",
    metaTitle: "Warranty claim NZ · CGA 1993 email template · PriceGuard",
    metaDescription:
      "Faulty product? In New Zealand the Consumer Guarantees Act 1993 protects you well past the manufacturer's warranty. PriceGuard files your receipt, watches the warranty clock, and drafts the CGA-anchored claim email - free.",
    keywords: [
      "warranty claim nz",
      "consumer guarantees act 1993",
      "cga claim email template",
      "how to claim a warranty in new zealand",
      "warranty claim email template nz",
      "extended warranty new zealand",
      "faulty product refund nz",
      "consumer rights new zealand",
      "fridge warranty claim nz",
      "tv warranty claim nz",
      "save money on repairs nz",
    ],
    h1Top: "Faulty product?",
    h1Bottom: "The CGA has your back longer than you think.",
    introOverline: "Warranty + receipt vault · free · New Zealand",
    introBody:
      "The manufacturer's warranty says one year. The Consumer Guarantees Act 1993 says products must be of acceptable quality and reasonable durability - which for a fridge is typically 7 to 10 years, a TV around 5 to 7, a kettle around 2. PriceGuard stores every receipt in your free vault, reminds you before the manufacturer's window expires, and drafts a polite CGA-anchored email when something fails - even years later. You don't need to know the law. We do.",
    metric: { value: "$420", label: "average warranty repair value", sub: "from CGA claims after the manufacturer window expired" },
    examples: [
      {
        title: "Samsung fridge · door hinge failure",
        retailer: "Harvey Norman",
        full: "Repair quoted $480",
        sale: "$0 (CGA repair)",
        save: "$480",
        note: "Fridge was 3 years old, outside the 2-year manufacturer warranty. CGA reasonable-durability standard for a $2,400 appliance is closer to 10 years - retailer arranged a free repair.",
      },
      {
        title: "Sony Bravia TV · panel line",
        retailer: "JB Hi-Fi",
        full: "Replacement quoted $1,899",
        sale: "$0 (CGA replacement)",
        save: "$1,899",
        note: "TV was 27 months old. CGA email referenced the price paid ($1,899) and reasonable durability - retailer replaced under guarantee.",
      },
      {
        title: "Dyson V11 vacuum · battery failure",
        retailer: "PB Tech",
        full: "Battery quoted $189",
        sale: "$0 (manufacturer goodwill)",
        save: "$189",
        note: "Inside the 2-year manufacturer window. Polite, well-drafted email got a replacement battery shipped without escalation.",
      },
    ],
    howItWorks: [
      { n: "01", t: "Vault your receipts", d: "Upload a photo or PDF when you buy something. We read it with AI, file it, and tag the warranty start date. Free forever, no card." },
      { n: "02", t: "We watch the warranty clock", d: "30 days before the typical manufacturer window expires, you get an email - your chance to flag anything wrong while the easy claim is still available." },
      { n: "03", t: "Draft a CGA claim any time", d: "Manufacturer warranty over? No problem. The CGA covers reasonable durability based on price + category. We draft the polite, accurate email for you to send." },
    ],
    faqs: [
      {
        q: "Is the Consumer Guarantees Act still valid after the manufacturer's warranty expires?",
        a: "Yes - and this is one of the most under-used consumer rights in New Zealand. The CGA 1993 guarantees that goods are of acceptable quality and reasonable durability, regardless of what the manufacturer's warranty says. For expensive durable goods like fridges, washing machines and TVs, courts have repeatedly held that 'reasonable durability' is many years - typically 5 to 10, depending on price and category.",
      },
      {
        q: "Do I need to keep the original receipt to claim under the CGA?",
        a: "Strictly speaking, no - the CGA applies whether you have the receipt or not. In practice, having the receipt makes the claim quick and frictionless. PriceGuard's free receipt vault means you never lose one - even if the paper version fades or you forgot which retailer sold it to you.",
      },
      {
        q: "What's the difference between the manufacturer warranty and the CGA?",
        a: "The manufacturer warranty is a private promise from the maker, usually 1-2 years. The CGA is New Zealand law that binds the retailer (not the manufacturer) to keep your purchase working for a reasonable lifetime. The retailer is your point of contact for both, but the CGA is the stronger and longer-lasting protection - and it's free.",
      },
      {
        q: "How do I write a polite CGA warranty claim email?",
        a: "Cite the Consumer Guarantees Act 1993, specifically the guarantees of acceptable quality (s6) and reasonable durability. Mention the purchase price and the typical lifetime expectation for that category. Ask for the most appropriate remedy: repair, replacement, or refund. Keep the tone respectful. PriceGuard drafts all of that for you automatically.",
      },
      {
        q: "Does PriceGuard pay for repairs or replacements?",
        a: "No - we don't insure your purchases, and we don't charge a percentage of the claim. We're a free tool that helps you exercise rights you already have under NZ law and retailer policies. The refund or repair comes directly from the retailer, in full, never through us.",
      },
    ],
    cta: {
      primary: "Vault a receipt free",
      primaryHref: "/signup",
      secondary: "See the FAQ on warranties",
      secondaryHref: "/faq",
    },
    crossLinks: [
      { label: "Planning to buy? Start a wishlist", href: "/save/wishlist" },
      { label: "Just bought it? Track for a refund", href: "/save/price-claim" },
    ],
  },
};

/**
 * Build the schema.org FAQPage JSON-LD blob for a given pillar.
 * Returned as a plain object so the page can `JSON.stringify` it into
 * a <script type="application/ld+json"> tag.
 */
export function buildFaqJsonLd(pillar) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: pillar.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}
